import React, { useState, useEffect } from 'react';
import { api } from '../api';

interface EventOption {
    start_time: string;
    end_time?: string;
    max_participants?: number;
}

interface FeedbackQuestion {
    type: 'stars' | 'text';
    question_text: string;
}

const AdminDashboard: React.FC<{ onLogout: () => void }> = ({ onLogout }) => {
    // --- CREATE FORM STATE ---
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [location, setLocation] = useState('');
    const [deadline, setDeadline] = useState('');
    const [isPublic, setIsPublic] = useState(true);
    const [votingType, setVotingType] = useState('simple');
    
    // Limits for Order Type
    const [limitFood, setLimitFood] = useState<number | ''>('');
    const [limitDrinks, setLimitDrinks] = useState<number | ''>('');
    const [limitLiters, setLimitLiters] = useState<number | ''>('');

    // Feedback State
    const [questions, setQuestions] = useState<FeedbackQuestion[]>([]);
    const [newQuestionType, setNewQuestionType] = useState<'stars' | 'text'>('stars');
    const [newQuestionText, setNewQuestionText] = useState('');

    // Public Link Settings
    const [publicLinkEnabled, setPublicLinkEnabled] = useState(false);
    const [allowNoInterest, setAllowNoInterest] = useState(false);
    const [logoFile, setLogoFile] = useState<File | null>(null);

    // Date Inputs
    const [inputDate, setInputDate] = useState('');
    const [inputEndDate, setInputEndDate] = useState(''); // New: End Date for multi-day
    const [inputStart, setInputStart] = useState('');
    const [inputEnd, setInputEnd] = useState('');
    const [inputMax, setInputMax] = useState<number | ''>('');
    const [splitDuration, setSplitDuration] = useState<number | ''>(''); 

    const [options, setOptions] = useState<EventOption[]>([]);
    const [message, setMessage] = useState<{type: string, text: string} | null>(null);

    // --- EVENT LIST STATE ---
    const [events, setEvents] = useState<any[]>([]);
    const [selectedEvent, setSelectedEvent] = useState<any | null>(null);
    const [editingEvent, setEditingEvent] = useState<any | null>(null);

    // --- INVITE STATE ---
    const [inviteEmails, setInviteEmails] = useState('');
    const [showInviteModal, setShowInviteModal] = useState<number | null>(null);
    const [showManualVote, setShowManualVote] = useState<boolean>(false);
    const [manualName, setManualName] = useState('');
    const [manualEmail, setManualEmail] = useState('');
    const [manualVotes, setManualVotes] = useState<Record<number, string>>({});
    // Manual Items
    const [manualItemVotes, setManualItemVotes] = useState<Record<number, number>>({});
    const [manualNoInterest, setManualNoInterest] = useState(false);

    // Shopping List Check (Local Storage)
    const [shoppingChecked, setShoppingChecked] = useState<Record<number, boolean>>({});

    // --- SMTP TEST STATE ---
    const [smtpStatus, setSmtpStatus] = useState<string>('');

    // --- NEW FEATURES STATE ---
    const [showEmailLogs, setShowEmailLogs] = useState<number | null>(null);
    const [emailLogs, setEmailLogs] = useState<any[]>([]);
    const [editingParticipant, setEditingParticipant] = useState<number | null>(null);
    const [editName, setEditName] = useState('');
    const [editMode, setEditMode] = useState(false);
    const [isCreateFormOpen, setIsCreateFormOpen] = useState(false);
    const [currentTime, setCurrentTime] = useState(new Date());

    // --- ITEM MANAGEMENT STATE ---
    const [items, setItems] = useState<any[]>([]);
    const [newItemName, setNewItemName] = useState('');
    const [newItemDesc, setNewItemDesc] = useState('');
    const [newItemType, setNewItemType] = useState('food');
    const [newItemLiters, setNewItemLiters] = useState<number | ''>('');
    const [newItemMaxPerUser, setNewItemMaxPerUser] = useState<number | ''>('');
    const [newItemImage, setNewItemImage] = useState<File | null>(null);
    const [editingItem, setEditingItem] = useState<any | null>(null);
    const [isProductsOpen, setIsProductsOpen] = useState(false);
    const [isShoppingListOpen, setIsShoppingListOpen] = useState(false);

    // --- REMINDER STATE ---
    const [reminderTime, setReminderTime] = useState('');
    const [reminderMessage, setReminderMessage] = useState('');

    // --- SORTING STATE ---
    const [prioritySortMode, setPrioritySortMode] = useState<'total' | 'first' | 'second'>('total');

    useEffect(() => {
        fetchEvents();
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Load Shopping List State
    
    useEffect(() => {
        const interval = setInterval(() => {
            if (selectedEvent && !editMode && !editingParticipant && !editingItem && !showInviteModal && !showManualVote) {
                viewEvent(selectedEvent.id, true, true);
            }
            if (!editingEvent && !isCreateFormOpen) {
                fetchEvents();
            }
        }, 10000); // 10 seconds
        return () => clearInterval(interval);
    }, [selectedEvent, editMode, editingParticipant, editingItem, showInviteModal, showManualVote, editingEvent, isCreateFormOpen]);

    useEffect(() => {
        if (selectedEvent) {
            const stored = localStorage.getItem(`shopping_${selectedEvent.id}`);
            if (stored) setShoppingChecked(JSON.parse(stored));
            else setShoppingChecked({});
        }
    }, [selectedEvent]);

    const toggleShoppingItem = (itemId: number) => {
        if (!selectedEvent) return;
        const newState = { ...shoppingChecked, [itemId]: !shoppingChecked[itemId] };
        setShoppingChecked(newState);
        localStorage.setItem(`shopping_${selectedEvent.id}`, JSON.stringify(newState));
    };

    const startEditing = (ev: any) => {
        setEditingEvent(ev);
        setTitle(ev.title);
        setDescription(ev.description || '');
        setLocation(ev.location || '');
        setDeadline(ev.deadline?.substring(0, 16) || '');
        setIsPublic(ev.is_public === 1);
        setVotingType(ev.voting_type || 'simple');
        setPublicLinkEnabled(ev.public_link_enabled === 1);
        setAllowNoInterest(ev.allow_no_interest === 1);
        setLimitFood(ev.limit_food || '');
        setLimitDrinks(ev.limit_drinks || '');
        setLimitLiters(ev.limit_liters || '');
        setOptions([]); // Hide options when editing metadata
        setQuestions([]);

        // Open and scroll to form
        setIsCreateFormOpen(true);
        setTimeout(() => {
            document.getElementById('createForm')?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
    };

    const cancelEditing = () => {
        setEditingEvent(null);
        setIsCreateFormOpen(false);
        // Reset form
        setTitle(''); 
        setDescription(''); 
        setLocation(''); 
        setDeadline('');
        setIsPublic(true);
        setVotingType('simple');
        setPublicLinkEnabled(false);
        setAllowNoInterest(false);
        setLimitFood('');
        setLimitDrinks('');
        setLimitLiters('');
        setOptions([]);
        setQuestions([]);
    };

    const testSmtp = async () => {
        setSmtpStatus('Teste SMTP...');
        try {
            await api.post('/admin/test-smtp', { target_email: '' });
            setSmtpStatus('✅ SMTP OK');
            setTimeout(() => setSmtpStatus(''), 5000);
        } catch (err: any) {
            setSmtpStatus(`❌ Fehler: ${err.response?.data?.error || err.message}`);
        }
    };

    const retryInvite = async (pId: number) => {
        if (!window.confirm("Einladung erneut senden?")) return;
        try {
            await api.post(`/participants/${pId}/retry`);
            alert("Erneut gesendet!");
            if (selectedEvent) viewEvent(selectedEvent.id);
        } catch (err: any) {
            alert(`Fehler: ${err.response?.data?.error || "Unbekannt"}`);
        }
    };

    const fetchEvents = async () => {
        try {
            const res = await api.get('/events');
            if (Array.isArray(res.data)) {
                setEvents(res.data);
            } else {
                setEvents([]);
            }
        } catch (err: any) {
            console.error("Failed to fetch events");
            if (err.response && err.response.status === 401) {
                onLogout(); // Auto-Logout bei ungültigem Token
            }
            setEvents([]);
        }
    };

    const handleAddOptions = () => {
        if (!inputDate || !inputStart) return;

        const newOptions: EventOption[] = [];
        const baseDate = inputDate;
        // Use inputEndDate if set, otherwise fallback to inputDate (same day)
        const endDateBase = inputEndDate || inputDate;
        
        // Construct Start Date Object
        const startTimeObj = new Date(`${baseDate}T${inputStart}`);

        // Construct End Date Object (only if time is given)
        let endTimeObj: Date | undefined;
        if (inputEnd) {
            endTimeObj = new Date(`${endDateBase}T${inputEnd}`);
            // Safety check: if end is before start, it might be wrong (or next day logic needed?)
            // Ideally user sets EndDate properly.
        }

        if (endTimeObj && splitDuration && Number(splitDuration) > 0) {
            let currTime = startTimeObj;
            const durationMs = Number(splitDuration) * 60 * 1000;

            while (currTime.getTime() + durationMs <= endTimeObj.getTime()) {
                const nextTime = new Date(currTime.getTime() + durationMs);
                newOptions.push({
                    start_time: currTime.toISOString(),
                    end_time: nextTime.toISOString(),
                    max_participants: inputMax ? Number(inputMax) : 0
                });
                currTime = nextTime;
            }
        } else {
            newOptions.push({
                start_time: startTimeObj.toISOString(),
                end_time: endTimeObj ? endTimeObj.toISOString() : undefined,
                max_participants: inputMax ? Number(inputMax) : 0
            });
        }
        setOptions([...options, ...newOptions]);
    };

    const removeOption = (index: number) => {
        setOptions(options.filter((_, i) => i !== index));
    };

    const handleAddQuestion = () => {
        if (!newQuestionText.trim()) return;
        setQuestions([...questions, { type: newQuestionType, question_text: newQuestionText.trim() }]);
        setNewQuestionText('');
    };

    const removeQuestion = (index: number) => {
        setQuestions(questions.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setMessage(null);

        if (!title) {
            setMessage({ type: 'danger', text: 'Bitte Titel eingeben.' });
            return;
        }

        if (votingType === 'feedback' && !editingEvent && questions.length === 0) {
            setMessage({ type: 'danger', text: 'Bitte mindestens eine Frage hinzufügen.' });
            return;
        }

        if (votingType !== 'order' && votingType !== 'feedback' && !editingEvent && options.length === 0) {
            setMessage({ type: 'danger', text: 'Bitte mindestens einen Termin hinzufügen.' });
            return;
        }

        try {
            const payload = {
                title, description, location, deadline: deadline || null, is_public: isPublic,
                public_link_enabled: publicLinkEnabled,
                allow_no_interest: allowNoInterest,
                options: options, questions: questions, voting_type: votingType,
                limit_food: limitFood || 0,
                limit_drinks: limitDrinks || 0,
                limit_liters: limitLiters || 0
            };

            if (editingEvent) {                // UPDATE LOGIC
                await api.put(`/events/${editingEvent.id}`, payload);
                if (logoFile) {
                    const formData = new FormData();
                    formData.append('logo', logoFile);
                    await api.post(`/events/${editingEvent.id}/logo`, formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                    });
                }
                setMessage({ type: 'success', text: `Event "${payload.title}" aktualisiert!` });
            } else {
                // CREATE LOGIC
                const res = await api.post('/events', payload);
                const eventId = res.data.id;

                if (logoFile) {
                    const formData = new FormData();
                    formData.append('logo', logoFile);
                    await api.post(`/events/${eventId}/logo`, formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                    });
                }
                 setMessage({ type: 'success', text: `Event "${res.data.title}" erstellt!` });
            }
            
            // Reset and Refresh List
            cancelEditing(); // Clears form and editing state
            fetchEvents();
        } catch (err: any) {
            console.error(err);
            if (err.response && err.response.status === 401) {
                onLogout();
                return;
            }
            const errorMsg = err.response?.data?.error || err.response?.data?.detail || 'Fehler beim Erstellen.';
            setMessage({ type: 'danger', text: errorMsg });
        }
    };

    const deleteEvent = async (id: number) => {
        if (!window.confirm("Wirklich löschen? Alle Daten gehen verloren.")) return;
        try {
            await api.delete(`/events/${id}`);
            fetchEvents();
            if (selectedEvent?.id === id) setSelectedEvent(null);
        } catch (err) {
            alert("Fehler beim Löschen");
        }
    };

    const viewEvent = async (id: number, keepEditMode = false, silent = false) => {
        try {
            const res = await api.get(`/events/${id}`);
            const data = res.data;
            setSelectedEvent(data);
            setItems(data.items || []);
            
            if (data.scheduled_reminder_at) {
                const date = new Date(data.scheduled_reminder_at);
                const offset = date.getTimezoneOffset() * 60000;
                const localISOTime = (new Date(date.getTime() - offset)).toISOString().slice(0, 16);
                setReminderTime(localISOTime);
            } else {
                setReminderTime('');
            }
            setReminderMessage(data.scheduled_reminder_message || '');

            if (!keepEditMode) setEditMode(false); // Reset edit mode protection if not requested to keep
            if (!silent) setTimeout(() => document.getElementById('event-details')?.scrollIntoView({ behavior: 'smooth' }), 100);
        } catch (err) {
            alert("Fehler beim Laden der Details");
        }
    };

    const saveReminder = async () => {
        if (!selectedEvent) return;
        let finalTime = null;
        if (reminderTime) {
             const date = new Date(reminderTime);
             finalTime = date.toISOString();
        }

        try {
            await api.post(`/events/${selectedEvent.id}/schedule-reminder`, { 
                time: finalTime, 
                message: reminderMessage 
            });
            alert("Erinnerung gespeichert!");
            viewEvent(selectedEvent.id, true);
        } catch (e) { alert("Fehler beim Speichern der Erinnerung"); }
    };

    const handleSendInvites = async () => {
        if (!showInviteModal || !inviteEmails) return;
        
        const lines = inviteEmails.split(/[\n,]+/).map(s => s.trim()).filter(s => s !== '');
        const participants = lines.map(line => {
            // Match "Name <email>" or just "email"
            const matchName = line.match(/(.*)<(.+@.+)>/);
            if (matchName) {
                return { name: matchName[1].trim(), email: matchName[2].trim() };
            }
            if (line.includes('@')) {
                return { name: '', email: line.trim() };
            }
            return null;
        }).filter(p => p !== null);
        
        if (participants.length === 0) {
            alert("Keine gültigen E-Mails gefunden. Format: email@test.de oder Name <email@test.de>");
            return;
        }

        try {
            const res = await api.post(`/events/${showInviteModal}/invite`, { participants });
            const sent = res.data.results.filter((r: any) => r.status === 'sent').length;
            const skipped = res.data.results.filter((r: any) => r.status === 'skipped').length;
            const failed = res.data.results.filter((r: any) => r.status === 'failed');
            
            let msg = `${sent} Einladungen erfolgreich versendet.`;
            if (skipped > 0) msg += `\n${skipped} übersprungen (bereits vorhanden).`;
            
            if (failed.length > 0) {
                msg += `\n\n⚠️ ${failed.length} Fehler beim Senden:`;
                failed.forEach((f: any) => {
                    msg += `\n- ${f.email}: ${f.reason}`;
                });
            }

            alert(msg);
            
            setInviteEmails('');
            setShowInviteModal(null);
            if (selectedEvent?.id === showInviteModal) viewEvent(showInviteModal);
        } catch (err) {
            alert("Fehler beim Senden der Anfrage.");
        }
    };

    const handleSendReminders = async (eventId: number) => {
        if (!window.confirm("Erinnerungs-Mails an alle versenden, die noch nicht abgestimmt haben?")) return;
        try {
            const res = await api.post(`/events/${eventId}/remind`);
            const sent = res.data.results.filter((r: any) => r.status === 'sent').length;
            alert(`${sent} Erinnerungen wurden versendet.`);
            viewEvent(eventId);
        } catch (err) {
            alert("Fehler beim Senden der Erinnerungen.");
        }
    };

    const finalizeEvent = async (eventId: number, optionId: number) => {
        const isUpdate = !!selectedEvent?.final_option_id;
        const msg = isUpdate 
            ? "Der finale Termin wurde bereits festgelegt. Möchtest du ihn auf diesen neuen Termin ÄNDERN und alle Teilnehmer informieren?"
            : "Diesen Termin als final festlegen und Teilnehmer informieren?";
            
        if (!window.confirm(msg)) return;
        try {
            await api.post(`/events/${eventId}/finalize`, { option_id: optionId });
            viewEvent(eventId); // Refresh details
        } catch (err) {
            alert("Fehler beim Finalisieren");
        }
    };

    const submitManualVote = async () => {
        if (!selectedEvent || !manualName) return;
        const votes = selectedEvent.options.map((opt: any) => ({
            option_id: opt.id,
            choice: manualVotes[opt.id] || 'no'
        }));
        try {
            await api.post(`/events/${selectedEvent.id}/manual-vote`, { name: manualName, email: manualEmail, votes, has_no_interest: manualNoInterest });
            alert("Stimme nachgetragen!");
            setManualName(''); setManualEmail(''); setManualVotes({}); setManualNoInterest(false); setShowManualVote(false);
            viewEvent(selectedEvent.id);
        } catch (err) { alert("Fehler"); }
    };

    const toggleMandatory = async (pId: number, eId: number) => {
        await api.post(`/participants/${pId}/toggle-mandatory`);
        viewEvent(eId, true);
    };

    const toggleNoInterest = async (pId: number, eId: number) => {
        await api.post(`/participants/${pId}/toggle-no-interest`);
        viewEvent(eId, true);
    };

    const deleteParticipant = async (pId: number, eId: number) => {
        if (!window.confirm("Diesen Teilnehmer wirklich entfernen?")) return;
        try {
            await api.delete(`/participants/${pId}`);
            viewEvent(eId, true);
        } catch (err) { alert("Fehler beim Löschen"); }
    };

    const addItem = async () => {
        if (!selectedEvent || !newItemName) return;
        try {
            const formData = new FormData();
            formData.append('name', newItemName);
            formData.append('description', newItemDesc);
            formData.append('type', newItemType);
            formData.append('liters', newItemLiters.toString());
            formData.append('max_per_user', newItemMaxPerUser.toString());
            if (newItemImage) formData.append('image', newItemImage);

            if (editingItem) {
                await api.put(`/events/${selectedEvent.id}/items/${editingItem.id}`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            } else {
                await api.post(`/events/${selectedEvent.id}/items`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            }
            
            setNewItemName(''); setNewItemDesc(''); setNewItemImage(null);
            setNewItemLiters(''); setNewItemMaxPerUser(''); setEditingItem(null);
            viewEvent(selectedEvent.id, true);
        } catch (e) { alert("Fehler beim Speichern"); }
    };

    const startEditingItem = (item: any) => {
        setEditingItem(item);
        setNewItemName(item.name);
        setNewItemDesc(item.description || '');
        setNewItemType(item.type || 'food');
        setNewItemLiters(item.liters || '');
        setNewItemMaxPerUser(item.max_per_user || '');
        setNewItemImage(null);
        document.getElementById('productForm')?.scrollIntoView({ behavior: 'smooth' });
    };

    const cancelEditingItem = () => {
        setEditingItem(null);
        setNewItemName('');
        setNewItemDesc('');
        setNewItemType('food');
        setNewItemLiters('');
        setNewItemMaxPerUser('');
        setNewItemImage(null);
    };

    const deleteItem = async (itemId: number) => {
        if (!window.confirm("Artikel löschen?")) return;
        try {
            await api.delete(`/events/${selectedEvent.id}/items/${itemId}`);
            viewEvent(selectedEvent.id, true);
        } catch (e) { alert("Fehler"); }
    };

    const moveItem = async (id: number, direction: 'up' | 'down') => {
        if (!selectedEvent) return;
        const newItems = [...items];
        const index = newItems.findIndex(i => i.id === id);
        if (index === -1) return;
        if (direction === 'up' && index === 0) return;
        if (direction === 'down' && index === newItems.length - 1) return;

        const neighborIndex = direction === 'up' ? index - 1 : index + 1;
        [newItems[index], newItems[neighborIndex]] = [newItems[neighborIndex], newItems[index]];

        try {
            await api.post(`/events/${selectedEvent.id}/reorder-items`, { order: newItems.map(i => i.id) });
            viewEvent(selectedEvent.id, true);
        } catch (e) { alert("Fehler beim Verschieben"); }
    };

    const deleteOption = async (id: number) => {
        if (!selectedEvent) return;
        if (!window.confirm("Option wirklich löschen? Alle zugehörigen Stimmen werden gelöscht.")) return;
        try {
            await api.delete(`/events/${selectedEvent.id}/options/${id}`);
            viewEvent(selectedEvent.id, true);
        } catch (e) { alert("Fehler beim Löschen"); }
    };

    const addOptionToExistingEvent = async () => {
        if (!selectedEvent || !inputDate || !inputStart) return;
        const baseDate = inputDate;
        const endDateBase = inputEndDate || inputDate;
        const startTimeObj = new Date(`${baseDate}T${inputStart}`);
        let endTimeObj;
        if (inputEnd) endTimeObj = new Date(`${endDateBase}T${inputEnd}`);
        
        try {
            await api.post(`/events/${selectedEvent.id}/options`, {
                start_time: startTimeObj.toISOString(),
                end_time: endTimeObj ? endTimeObj.toISOString() : undefined
            });
            setInputDate(''); setInputStart(''); setInputEndDate(''); setInputEnd('');
            viewEvent(selectedEvent.id, true);
        } catch (e) { alert("Fehler beim Hinzufügen"); }
    };

    const moveOption = async (id: number, direction: 'up' | 'down') => {
        if (!selectedEvent) return;
        const newOptions = [...selectedEvent.options];
        const index = newOptions.findIndex((o: any) => o.id === id);
        if (index === -1) return;
        if (direction === 'up' && index === 0) return;
        if (direction === 'down' && index === newOptions.length - 1) return;

        const neighborIndex = direction === 'up' ? index - 1 : index + 1;
        [newOptions[index], newOptions[neighborIndex]] = [newOptions[neighborIndex], newOptions[index]];

        try {
            await api.post(`/events/${selectedEvent.id}/reorder-options`, { order: newOptions.map((o: any) => o.id) });
            viewEvent(selectedEvent.id, true);
        } catch (e) { alert("Fehler beim Verschieben"); }
    };

    const formatDatePreview = (iso: string) => new Date(iso).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });
    const formatPreview = (iso: string) => new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    const formatOptionHeader = (opt: any) => {
        const start = new Date(opt.start_time);
        const dateStr = start.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
        const timeStart = start.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
        return `${dateStr} ${timeStart}`;
    };

    // --- NEW HELPERS ---
    const loadEmailLogs = async (eventId: number) => {
        try {
            const res = await api.get(`/events/${eventId}/email-logs`);
            setEmailLogs(res.data);
            setShowEmailLogs(eventId);
        } catch (e) { alert("Fehler beim Laden der Logs"); }
    };

    const startEditingParticipant = (p: any) => {
        setEditingParticipant(p.id);
        setEditName(p.name);
    };

    const saveParticipantName = async (pId: number) => {
        try {
            await api.put(`/participants/${pId}/name`, { name: editName });
            setEditingParticipant(null);
            if (selectedEvent) viewEvent(selectedEvent.id, true);
        } catch (e) { alert("Fehler beim Speichern"); }
    };

    const [editPopup, setEditPopup] = useState<{ x: number, y: number, pId: number, optId: number } | null>(null);

    const handleVotePopup = (e: React.MouseEvent, p: any, optionId: number) => {
        if (!editMode) return;
        const rect = e.currentTarget.getBoundingClientRect();
        setEditPopup({
            x: rect.left,
            y: rect.bottom, // display below the cell
            pId: p.id,
            optId: optionId
        });
    };

    const handleVoteSelect = async (choice: string) => {
        if (!editPopup || !selectedEvent) return;
        const { pId, optId } = editPopup;
        setEditPopup(null); // Close immediately or wait? Better close immediately for responsiveness.

        const p = selectedEvent.participants.find((part: any) => part.id === pId);
        if (!p) return;

        const newVotes = selectedEvent.options.map((opt: any) => {
            const existing = Array.isArray(p.votes) ? p.votes.find((v: any) => v.option_id === opt.id) : null;
            let c = existing?.choice || 'no'; 
            if (opt.id === optId) c = choice;
            return { option_id: opt.id, choice: c };
        });

        try {
            await api.put(`/participants/${pId}/votes`, { votes: newVotes });
            viewEvent(selectedEvent.id, true);
        } catch (e) { alert("Fehler beim Speichern"); }
    };

    return (
        <div className="main-container mt-4 mb-5">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h1 className="display-5">Admin Dashboard</h1>
                    <div className="text-muted small">
                        🕒 Aktuelle Zeit: {currentTime.toLocaleString('de-DE', { 
                            weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric',
                            hour: '2-digit', minute: '2-digit', second: '2-digit', 
                            timeZoneName: 'short' 
                        })}
                    </div>
                </div>
                <div className="d-flex align-items-center gap-2">
                    {smtpStatus && <span className="badge bg-warning text-dark">{smtpStatus}</span>}
                    <button className="btn btn-sm btn-outline-info" onClick={testSmtp}>📧 SMTP Testen</button>
                    <button onClick={onLogout} className="btn btn-outline-danger">Abmelden</button>
                </div>
            </div>

            {message && <div className={`alert alert-${message.type}`}>{message.text}</div>}

            {/* CREATE EVENT ACCORDION */}
            <div className="card mb-5">
                <div className="card-header cursor-pointer" style={{cursor: 'pointer'}} onClick={() => setIsCreateFormOpen(!isCreateFormOpen)}>
                    <h4 className="mb-0 d-flex justify-content-between">
                        <span>{editingEvent ? '✏️ Event bearbeiten' : '➕ Neues Event erstellen'}</span>
                        <small>{isCreateFormOpen ? '▲' : '▼'}</small>
                    </h4>
                </div>
                <div id="createForm" className={`collapse ${isCreateFormOpen ? 'show' : ''}`}> 
                <div className="card-body">
                    <form onSubmit={handleSubmit}>
                        <div className="row">
                            <div className="col-md-6 mb-3">
                                <label className="form-label">Titel</label>
                                <input type="text" className="form-control" value={title} onChange={e => setTitle(e.target.value)} required />
                            </div>
                            <div className="col-md-6 mb-3">
                                <label className="form-label">Ort</label>
                                <input type="text" className="form-control" value={location} onChange={e => setLocation(e.target.value)} />
                            </div>
                        </div>

                        <div className="mb-3">
                            <label className="form-label">Beschreibung</label>
                            <textarea className="form-control" rows={3} value={description} onChange={e => setDescription(e.target.value)}></textarea>
                        </div>

                        <div className="row mb-3">
                            <div className="col-md-4">
                                <label className="form-label">Logo (Branding)</label>
                                <input type="file" className="form-control" accept="image/*" onChange={e => setLogoFile(e.target.files ? e.target.files[0] : null)} />
                            </div>
                            <div className="col-md-4">
                                <label className="form-label">Rückmeldefrist (Deadline)</label>
                                <input type="datetime-local" className="form-control" value={deadline} onChange={e => setDeadline(e.target.value)} />
                            </div>
                            <div className="col-md-4">
                                <label className="form-label">Sichtbarkeit</label>
                                <div className="form-check form-switch mt-2">
                                    <input className="form-check-input" type="checkbox" checked={isPublic} onChange={e => setIsPublic(e.target.checked)} />
                                    <label className="form-check-label">{isPublic ? 'Öffentlich' : 'Geheim (Admin)'}</label>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <label className="form-label">Abstimmungs-Modus</label>
                                <select className="form-select" value={votingType} onChange={e => setVotingType(e.target.value)}>
                                    <option value="simple">Ja / Nein / Vielleicht</option>
                                    <option value="priority">Priorität (1. / 2. Wahl)</option>
                                    <option value="order">Bestellung (Frühstück)</option>
                                    <option value="feedback">Feedback-Bogen</option>
                                </select>
                            </div>
                        </div>

                        {votingType === 'order' && (
                            <div className="row mb-3">
                                <div className="col-md-4">
                                    <label className="form-label">Limit: Essen (Stück/Person)</label>
                                    <input type="number" className="form-control" placeholder="0 = ∞" value={limitFood} onChange={e => setLimitFood(e.target.value ? Number(e.target.value) : '')} />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label">Limit: Getränke (Stück/Person)</label>
                                    <input type="number" className="form-control" placeholder="0 = ∞" value={limitDrinks} onChange={e => setLimitDrinks(e.target.value ? Number(e.target.value) : '')} />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label">Limit: Liter Getränke (pro Person)</label>
                                    <input type="number" step="0.1" className="form-control" placeholder="0 = ∞" value={limitLiters} onChange={e => setLimitLiters(e.target.value ? Number(e.target.value) : '')} />
                                </div>
                            </div>
                        )}

                        {votingType === 'feedback' && !editingEvent && (
                            <>
                                <hr />
                                <h5>Feedback-Fragen</h5>
                                <div className="card mb-3">
                                    <div className="card-body">
                                        <div className="row g-2 align-items-end">
                                            <div className="col-md-3">
                                                <label className="form-label small">Fragetyp</label>
                                                <select className="form-select" value={newQuestionType} onChange={e => setNewQuestionType(e.target.value as 'stars' | 'text')}>
                                                    <option value="stars">Sterne (1-5)</option>
                                                    <option value="text">Freitext</option>
                                                </select>
                                            </div>
                                            <div className="col-md-7">
                                                <label className="form-label small">Frage</label>
                                                <input type="text" className="form-control" value={newQuestionText} onChange={e => setNewQuestionText(e.target.value)} placeholder="z.B. Wie hat es dir gefallen?" />
                                            </div>
                                            <div className="col-md-2">
                                                <button type="button" className="btn btn-primary w-100" onClick={handleAddQuestion} disabled={!newQuestionText.trim()}>Hinzufügen</button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {questions.length > 0 && (
                                    <ul className="list-group mb-4">
                                        {questions.map((q, idx) => (
                                            <li key={idx} className="list-group-item d-flex justify-content-between align-items-center">
                                                <span><strong>{q.type === 'stars' ? '⭐' : '📝'}</strong> {q.question_text}</span>
                                                <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeQuestion(idx)}>Löschen</button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </>
                        )}

                        {votingType !== 'order' && votingType !== 'feedback' && !editingEvent && (
                            <>
                                <hr />
                                <h5>Termin-Optionen</h5>
                                <div className="card mb-3">
                                    <div className="card-body">
                                        <div className="row g-2 align-items-end">
                                            <div className="col-md-3">
                                                <label className="form-label small">Datum Start</label>
                                                <input type="date" className="form-control" value={inputDate} onChange={e => setInputDate(e.target.value)} />
                                            </div>
                                            <div className="col-md-2">
                                                <label className="form-label small">Von</label>
                                                <input type="time" className="form-control" value={inputStart} onChange={e => setInputStart(e.target.value)} />
                                            </div>
                                            <div className="col-md-3">
                                                <label className="form-label small">Datum Ende (Optional)</label>
                                                <input type="date" className="form-control" value={inputEndDate} onChange={e => setInputEndDate(e.target.value)} />
                                            </div>
                                            <div className="col-md-2">
                                                <label className="form-label small">Bis</label>
                                                <input type="time" className="form-control" value={inputEnd} onChange={e => setInputEnd(e.target.value)} />
                                            </div>
                                            <div className="col-md-2">
                                                <label className="form-label small">Limit</label>
                                                <input type="number" className="form-control" placeholder="0 = ∞" value={inputMax} onChange={e => setInputMax(e.target.value ? Number(e.target.value) : '')} />
                                            </div>
                                            <div className="col-md-3">
                                                <label className="form-label small">Split (Min)</label>
                                                <input type="number" className="form-control" placeholder="Optional" value={splitDuration} onChange={e => setSplitDuration(e.target.value ? Number(e.target.value) : '')} />
                                            </div>
                                            <div className="col-md-2">
                                                <button type="button" className="btn btn-primary w-100" onClick={handleAddOptions} disabled={!inputDate || !inputStart}>Add</button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {options.length > 0 && (
                                    <div className="mb-4">
                                        {options.map((opt, idx) => (
                                            <span key={idx} className="badge bg-secondary me-2 mb-2 p-2">
                                                {formatDatePreview(opt.start_time)} {formatPreview(opt.start_time)} 
                                                {opt.end_time && ` - ${formatDatePreview(opt.end_time) !== formatDatePreview(opt.start_time) ? formatDatePreview(opt.end_time) + ' ' : ''}${formatPreview(opt.end_time)}`}
                                                <span style={{cursor:'pointer', marginLeft:'10px'}} onClick={() => removeOption(idx)}>x</span>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                        
                        <hr />
                        <div className="row mb-3">
                             <div className="col-md-6">
                                <div className="form-check form-switch mt-2">
                                    <input className="form-check-input" type="checkbox" checked={publicLinkEnabled} onChange={e => setPublicLinkEnabled(e.target.checked)} />
                                    <label className="form-check-label">Verteiler-Link aktivieren</label>
                                </div>
                                <div className="form-check form-switch mt-2">
                                    <input className="form-check-input" type="checkbox" checked={allowNoInterest} onChange={e => setAllowNoInterest(e.target.checked)} />
                                    <label className="form-check-label">Option "Kein Interesse" zulassen</label>
                                </div>
                             </div>
                        </div>
                        <div className="d-flex justify-content-end gap-2">
                            {editingEvent && <button type="button" className="btn btn-secondary" onClick={cancelEditing}>Abbrechen</button>}
                            <button type="submit" className="btn btn-success w-100">{editingEvent ? 'Änderungen speichern' : 'Event erstellen'}</button>
                        </div>
                    </form>
                </div>
                </div>
            </div>

            {/* --- LIST OF EVENTS --- */}
            <h3 className="mb-3">Deine Abstimmungen</h3>
            <div className="table-responsive">
                <table className="table table-hover mb-0 align-middle">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Titel</th>
                            <th>Erstellt am</th>
                            <th>Links</th>
                            <th>Aktion</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(!Array.isArray(events) || events.length === 0) && <tr><td colSpan={5} className="text-center p-3">Noch keine Events erstellt.</td></tr>}
                        {Array.isArray(events) && events.map(ev => (
                            <tr key={ev.id}>
                                <td>{ev.id}</td>
                                <td className="fw-bold">{ev.title}</td>
                                <td>{new Date(ev.created_at).toLocaleDateString('de-DE')}</td>
                                <td>
                                    {ev.public_link_enabled && ev.public_token ? (
                                        <div className="input-group input-group-sm" style={{maxWidth: '250px'}}>
                                            <input type="text" className="form-control" readOnly value={`${window.location.origin}/public/${ev.public_token}`} />
                                            <button className="btn btn-outline-secondary" onClick={() => navigator.clipboard.writeText(`${window.location.origin}/public/${ev.public_token}`)}>Copy</button>
                                        </div>
                                    ) : <span className="text-muted small">Nur Einladung</span>}
                                </td>
                                <td>
                                    <button className="btn btn-sm btn-primary me-4" onClick={() => viewEvent(ev.id)}>Ergebnisse</button>
                                    <button className="btn btn-sm btn-info me-2 text-white" onClick={() => setShowInviteModal(ev.id)}>📩 Einladen</button>
                                    <button className="btn btn-sm btn-secondary me-2" onClick={() => loadEmailLogs(ev.id)}>📜 Logs</button>
                                    <button className="btn btn-sm btn-outline-secondary me-2" onClick={() => startEditing(ev)}>✏️</button>
                                    <button className="btn btn-sm btn-danger" onClick={() => deleteEvent(ev.id)}>🗑️</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* --- INVITE MODAL (Simple Inline) --- */}
            {showEmailLogs && (
                <div className="card mt-4 border-secondary shadow">
                    <div className="card-header bg-secondary text-white d-flex justify-content-between">
                        <h5 className="mb-0">E-Mail Logs (Event ID: {showEmailLogs})</h5>
                        <button className="btn btn-sm btn-light" onClick={() => setShowEmailLogs(null)}>X</button>
                    </div>
                    <div className="card-body p-0">
                        <div className="table-responsive" style={{maxHeight: '300px'}}>
                            <table className="table table-striped table-sm mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th>Zeit</th>
                                        <th>Empfänger</th>
                                        <th>Betreff</th>
                                        <th>Status</th>
                                        <th>Fehler</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {emailLogs.length === 0 ? <tr><td colSpan={5} className="text-center">Keine Logs vorhanden.</td></tr> : emailLogs.map((log: any) => (
                                        <tr key={log.id}>
                                            <td>{new Date(log.timestamp.replace(' ', 'T') + 'Z').toLocaleString('de-DE')}</td>
                                            <td>{log.recipient}</td>
                                            <td>{log.subject}</td>
                                            <td>
                                                <span className={`badge bg-${log.status === 'sent' ? 'success' : 'danger'}`}>{log.status}</span>
                                            </td>
                                            <td className="text-danger small">{log.error}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {showInviteModal && (
                <div className="card mt-4 border-info shadow">
                    <div className="card-header bg-info text-white">
                        <h5 className="mb-0">Teilnehmer einladen (Event ID: {showInviteModal})</h5>
                    </div>
                    <div className="card-body">
                        <p className="small text-muted">Gib E-Mail-Adressen ein (mit Komma getrennt). Mehrere Namen pro Mail möglich (z.B. Familie).</p>
                        <textarea 
                            className="form-control mb-3" 
                            rows={3} 
                            placeholder="Max Mustermann <max@test.de>, Anna <max@test.de>"
                            value={inviteEmails} 
                            onChange={e => setInviteEmails(e.target.value)} 
                        />
                        <button className="btn btn-info text-white me-2" onClick={handleSendInvites}>Einladungen senden</button>
                        <button className="btn btn-secondary" onClick={() => setShowInviteModal(null)}>Abbrechen</button>
                    </div>
                </div>
            )}

            {/* --- DETAILS VIEW --- */}
            {selectedEvent && (
                <div id="event-details" className="card mt-5 border-primary">
                    <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                        <h4 className="mb-0">Ergebnisse: {selectedEvent.title}</h4>
                        <div>
                            <a href={`${api.defaults.baseURL}/events/${selectedEvent.id}/export?token=${localStorage.getItem('adminToken')}`} className="btn btn-sm btn-success me-2">📊 Export CSV</a>
                            <button className="btn btn-sm btn-warning me-2" onClick={() => handleSendReminders(selectedEvent.id)}>🔔 Erinnerungen senden</button>
                            <button className="btn btn-sm btn-light" onClick={() => setSelectedEvent(null)}>Schließen</button>
                        </div>
                    </div>
                    <div className="card-body">
                         
                         <div className="mb-3 d-flex align-items-center justify-content-between">
                             <button className="btn btn-sm btn-outline-primary" onClick={() => setShowManualVote(!showManualVote)}>
                                 {showManualVote ? 'Abbrechen' : '➕ Stimme manuell nachtragen (z.B. Telefon)'}
                             </button>
                             <div className="form-check form-switch">
                                 <input className="form-check-input" type="checkbox" id="editModeSwitch" checked={editMode} onChange={e => setEditMode(e.target.checked)} />
                                 <label className="form-check-label" htmlFor="editModeSwitch">Bearbeitungsmodus (Schutz)</label>
                             </div>
                         </div>

                         {showManualVote && (
                             <div className="card mb-4 border-primary">
                                 <div className="card-body">
                                     <h5>Manuelle Stimme</h5>
                                     <div className="row mb-3">
                                         <div className="col-md-6"><input type="text" className="form-control" placeholder="Name" value={manualName} onChange={e => setManualName(e.target.value)} /></div>
                                         <div className="col-md-6"><input type="email" className="form-control" placeholder="Email (optional)" value={manualEmail} onChange={e => setManualEmail(e.target.value)} /></div>
                                     </div>

                                     {selectedEvent.allow_no_interest === 1 && (
                                        <div className="form-check mb-3">
                                            <input className="form-check-input" type="checkbox" id="manualNoInterest" checked={manualNoInterest} onChange={e => setManualNoInterest(e.target.checked)} />
                                            <label className="form-check-label text-danger" htmlFor="manualNoInterest">
                                                Hat generell abgesagt (Kein Interesse)
                                            </label>
                                        </div>
                                     )}
                                     
                                     {!manualNoInterest && (
                                         selectedEvent.voting_type === 'order' ? (
                                             <div className="row row-cols-2 g-2">
                                                 {items.map((item: any) => (
                                                     <div key={item.id} className="col d-flex justify-content-between align-items-center border p-2 rounded">
                                                         <span>{item.name}</span>
                                                         <input 
                                                             type="number" 
                                                             className="form-control form-control-sm" 
                                                             style={{width: '60px'}} 
                                                             value={manualItemVotes[item.id] || 0} 
                                                             onChange={e => setManualItemVotes({...manualItemVotes, [item.id]: Number(e.target.value)})} 
                                                         />
                                                     </div>
                                                 ))}
                                             </div>
                                         ) : (
                                             <div className="table-responsive">
                                                 <table className="table table-sm">
                                                     <tbody>
                                                         {selectedEvent.options.map((opt: any) => (
                                                             <tr key={opt.id}>
                                                                 <td>{formatOptionHeader(opt)}</td>
                                                                 <td>
                                                                     <select className="form-select form-select-sm" value={manualVotes[opt.id] || 'no'} onChange={e => setManualVotes({...manualVotes, [opt.id]: e.target.value})}>
                                                                         <option value="yes">Ja / 1. Wahl</option>
                                                                         <option value="maybe">Vielleicht / 2. Wahl</option>
                                                                         <option value="no">Nein</option>
                                                                     </select>
                                                                 </td>
                                                             </tr>
                                                         ))}
                                                     </tbody>
                                                 </table>
                                             </div>
                                         )
                                     )}
                                     
                                     <button className="btn btn-primary mt-3" onClick={() => {
                                         if (selectedEvent.voting_type === 'order') {
                                             // Convert manualItemVotes map to array
                                             const voteArr = items.map(i => ({ item_id: i.id, quantity: manualItemVotes[i.id] || 0 }));
                                              api.post(`/events/${selectedEvent.id}/manual-vote`, { name: manualName, email: manualEmail, item_votes: voteArr, has_no_interest: manualNoInterest })
                                              .then(() => { alert("Gespeichert"); setManualName(''); setManualItemVotes({}); setManualNoInterest(false); setShowManualVote(false); viewEvent(selectedEvent.id); })
                                              .catch(() => alert("Fehler"));
                                         } else {
                                             submitManualVote();
                                         }
                                     }}>Speichern</button>
                                 </div>
                             </div>
                         )}

                         {/* SCHEDULED REMINDER UI */}
                         {selectedEvent.final_option_id && (
                             <div className="card mb-4 border-warning">
                                 <div className="card-header bg-warning text-dark">
                                     <h5 className="mb-0">🔔 Automatische Erinnerung vor dem Termin</h5>
                                 </div>
                                 <div className="card-body">
                                     <p className="small text-muted">Sende automatisch eine E-Mail an alle Teilnehmer (die zugesagt haben), bevor der Termin stattfindet.</p>
                                     <div className="row mb-3">
                                         <div className="col-md-5">
                                             <label className="form-label">Zeitpunkt der Erinnerung</label>
                                             <input 
                                                 type="datetime-local" 
                                                 className="form-control" 
                                                 value={reminderTime} 
                                                 onChange={e => setReminderTime(e.target.value)} 
                                             />
                                         </div>
                                         <div className="col-md-7">
                                             <label className="form-label">Zusätzliche Nachricht</label>
                                             <textarea 
                                                 className="form-control" 
                                                 rows={1} 
                                                 value={reminderMessage} 
                                                 placeholder="Z.B.: Bitte denkt an Sportsachen!" 
                                                 onChange={e => setReminderMessage(e.target.value)} 
                                             />
                                         </div>
                                     </div>
                                     <div className="d-flex justify-content-between align-items-center">
                                         <div>
                                             {selectedEvent.scheduled_reminder_sent === 1 ? (
                                                 <span className="badge bg-success">✅ Bereits gesendet</span>
                                             ) : (
                                                 selectedEvent.scheduled_reminder_at && <span className="badge bg-info text-dark">⏳ Geplant</span>
                                             )}
                                         </div>
                                         <div>
                                             <button className="btn btn-warning" onClick={saveReminder}>Speichern</button>
                                             {selectedEvent.scheduled_reminder_at && <button className="btn btn-outline-danger ms-2" onClick={() => { setReminderTime(''); setTimeout(saveReminder, 100); }}>Löschen</button>}
                                         </div>
                                     </div>
                                 </div>
                             </div>
                         )}

                         {selectedEvent.voting_type === 'order' ? (
                             <div className="mt-4">
                                 {/* --- PRODUCT MANAGEMENT --- */}
                                 <h5 
                                     className="border-bottom pb-2" 
                                     style={{cursor: 'pointer'}} 
                                     onClick={() => setIsProductsOpen(!isProductsOpen)}
                                 >
                                     <div className="d-flex justify-content-between align-items-center">
                                         <span>Produkte / Auswahlmöglichkeiten</span>
                                         <small>{isProductsOpen ? '▲' : '▼'}</small>
                                     </div>
                                 </h5>
                                 <div className={`collapse ${isProductsOpen ? 'show' : ''}`} id="productForm">
                                 <div className="card mb-4">
                                     <div className="card-body">
                                         <div className="row g-2 align-items-end">
                                             <div className="col-md-2">
                                                 <label className="form-label small">Name</label>
                                                 <input type="text" className="form-control" value={newItemName} onChange={e => setNewItemName(e.target.value)} />
                                             </div>
                                             <div className="col-md-2">
                                                 <label className="form-label small">Beschreibung</label>
                                                 <input type="text" className="form-control" value={newItemDesc} onChange={e => setNewItemDesc(e.target.value)} />
                                             </div>
                                             <div className="col-md-2">
                                                 <label className="form-label small">Typ</label>
                                                 <select className="form-select" value={newItemType} onChange={e => setNewItemType(e.target.value)}>
                                                     <option value="food">Essen</option>
                                                     <option value="drink">Getränk</option>
                                                 </select>
                                             </div>
                                             {newItemType === 'drink' && (
                                                <>
                                                    <div className="col-md-1">
                                                        <label className="form-label small" title="Liter pro Stück">Liter</label>
                                                        <input type="number" step="0.1" className="form-control" placeholder="z.B. 0.5" value={newItemLiters} onChange={e => setNewItemLiters(e.target.value ? Number(e.target.value) : '')} />
                                                    </div>
                                                    <div className="col-md-1">
                                                        <label className="form-label small" title="Max. Stückzahl pro Person">Max/Pers.</label>
                                                        <input type="number" className="form-control" placeholder="∞" value={newItemMaxPerUser} onChange={e => setNewItemMaxPerUser(e.target.value ? Number(e.target.value) : '')} />
                                                    </div>
                                                </>
                                             )}
                                             <div className="col-md-2">
                                                 <label className="form-label small">Bild {editingItem && '(Optional)'}</label>
                                                 <input type="file" className="form-control" accept="image/*" onChange={e => setNewItemImage(e.target.files ? e.target.files[0] : null)} />
                                             </div>
                                             <div className="col-md-2 d-flex gap-2">
                                                 <button className="btn btn-primary flex-grow-1" onClick={addItem} disabled={!newItemName}>{editingItem ? 'Speichern' : 'Hinzufügen'}</button>
                                                 {editingItem && <button className="btn btn-secondary" onClick={cancelEditingItem}>Abbrechen</button>}
                                             </div>
                                         </div>
                                     </div>
                                 </div>
                                 
                                 {/* ITEMS LIST */}
                                 <div className="row row-cols-2 row-cols-md-4 g-3 mb-5">
                                     {items.map((item: any) => (
                                         <div key={item.id} className="col">
                                             <div className="card h-100 shadow-sm">
                                                 {item.image_path ? (
                                                     <img src={`${window.location.origin}${item.image_path}`} className="card-img-top" alt={item.name} style={{height: '150px', objectFit: 'contain'}} />
                                                 ) : (
                                                     <div className="card-img-top bg-secondary text-white d-flex align-items-center justify-content-center" style={{height: '150px'}}>Kein Bild</div>
                                                 )}
                                                 <div className="card-body p-2">
                                                     <h6 className="card-title">{item.name}</h6>
                                                     <p className="card-text small text-muted mb-1" style={{ whiteSpace: 'pre-wrap' }}>{item.description}</p>
                                                     <div className="d-flex justify-content-between align-items-center">
                                                         <span className={`badge bg-${item.type === 'food' ? 'warning' : 'info'} text-dark`}>{item.type === 'food' ? 'Essen' : 'Trinken'}</span>
                                                         <div className="btn-group">
                                                             <button className="btn btn-xs btn-outline-secondary py-0 px-1" onClick={() => moveItem(item.id, 'up')}>↑</button>
                                                             <button className="btn btn-xs btn-outline-secondary py-0 px-1" onClick={() => moveItem(item.id, 'down')}>↓</button>
                                                             <button className="btn btn-sm btn-outline-primary ms-1 py-0 px-1" onClick={() => startEditingItem(item)}>✏️</button>
                                                             <button className="btn btn-sm btn-outline-danger ms-1 py-0 px-1" onClick={() => deleteItem(item.id)}>🗑️</button>
                                                         </div>
                                                     </div>
                                                 </div>
                                             </div>
                                         </div>
                                     ))}
                                 </div>
                                 </div>

                                 {/* --- SHOPPING LIST --- */}
                                 <h5 
                                     className="border-bottom pb-2" 
                                     style={{cursor: 'pointer'}} 
                                     onClick={() => setIsShoppingListOpen(!isShoppingListOpen)}
                                 >
                                     <div className="d-flex justify-content-between align-items-center">
                                         <span>Einkaufsliste (Gesamtbedarf)</span>
                                         <small>{isShoppingListOpen ? '▲' : '▼'}</small>
                                     </div>
                                 </h5>
                                 <div className={`collapse ${isShoppingListOpen ? 'show' : ''}`}>
                                 <div className="alert alert-info">
                                     <p className="small mb-2 text-muted">Klicken zum Abhakten (wird lokal gespeichert).</p>
                                     <div className="row">
                                         {items.map((item: any) => {
                                             const sum = selectedEvent.participants ? selectedEvent.participants.reduce((acc: number, p: any) => {
                                                 const v = p.item_votes ? p.item_votes.find((iv: any) => iv.item_id === item.id) : null;
                                                 return acc + (v ? v.quantity : 0);
                                             }, 0) : 0;
                                             
                                             if (sum === 0) return null; // Hide items with 0 total
                                             
                                             const isChecked = shoppingChecked[item.id];

                                             return (
                                                 <div 
                                                     key={item.id} 
                                                     className="col-md-3 mb-2 cursor-pointer user-select-none" 
                                                     style={{
                                                         cursor: 'pointer', 
                                                         textDecoration: isChecked ? 'line-through' : 'none',
                                                         opacity: isChecked ? 0.5 : 1
                                                     }}
                                                     onClick={() => toggleShoppingItem(item.id)}
                                                 >
                                                     <span className="me-2">{isChecked ? '✅' : '⬜'}</span>
                                                     <strong>{sum}x</strong> {item.name}
                                                 </div>
                                             );
                                         })}
                                     </div>
                                 </div>
                                 </div>

                                 {/* --- ORDERS TABLE --- */}
                                 <h5 className="mt-4">Bestellungen pro Teilnehmer</h5>
                                 <div className="table-responsive">
                                     <table className="table text-center align-middle table-hover">
                                         <thead className="table-light">
                                             <tr>
                                                 <th>Teilnehmer</th>
                                                 {items.map((item: any) => (
                                                     <th key={item.id} style={{minWidth: '100px'}}>
                                                         {item.name}
                                                     </th>
                                                 ))}
                                             </tr>
                                         </thead>
                                         <tbody>
                                             {(!selectedEvent.participants || selectedEvent.participants.length === 0) && (
                                                 <tr><td colSpan={items.length + 1}>Noch keine Teilnehmer.</td></tr>
                                             )}
                                             {selectedEvent.participants && selectedEvent.participants.map((p: any) => (
                                                 <tr key={p.id}>
                                                     <td className="text-start">
                                                         <strong>{p.name}</strong> <small className="text-muted">({p.email})</small>
                                                         {p.comment && <div className="small text-info">{p.comment}</div>}
                                                         <div className="mt-1">
                                                             <button className={`btn btn-sm ${p.has_no_interest ? 'btn-danger' : 'btn-outline-danger'} py-0 px-1 me-1`} style={{fontSize: '0.6rem'}} onClick={() => toggleNoInterest(p.id, selectedEvent.id)}>
                                                                 Kein Interesse
                                                             </button>
                                                             <button className="btn btn-sm btn-outline-danger py-0 px-1" style={{fontSize: '0.6rem'}} onClick={() => deleteParticipant(p.id, selectedEvent.id)}>🗑️</button>
                                                         </div>
                                                     </td>
                                                     {p.has_no_interest ? (
                                                        <td colSpan={items.length} className="text-danger fw-bold bg-danger-subtle align-middle fs-6">
                                                            Kein Interesse am Thema
                                                        </td>
                                                     ) : (
                                                        items.map((item: any) => {
                                                            const vote = p.item_votes ? p.item_votes.find((v: any) => v.item_id === item.id) : null;
                                                            const qty = vote ? vote.quantity : 0;
                                                            return (
                                                                <td key={item.id} className={qty > 0 ? 'fw-bold bg-success bg-opacity-10' : 'text-muted'}>
                                                                    {qty}
                                                                </td>
                                                            );
                                                        })
                                                     )}
                                                 </tr>
                                             ))}
                                         </tbody>
                                     </table>
                                 </div>
                             </div>
                         ) : selectedEvent.voting_type === 'feedback' ? (
                             <div className="mt-4">
                                 <h5>Feedback-Ergebnisse</h5>
                                 <div className="table-responsive">
                                     <table className="table align-middle table-hover">
                                         <thead className="table-light">
                                             <tr>
                                                 <th>Teilnehmer</th>
                                                 {selectedEvent.questions && selectedEvent.questions.map((q: any) => (
                                                     <th key={q.id}>{q.question_text}</th>
                                                 ))}
                                             </tr>
                                         </thead>
                                         <tbody>
                                             {(!selectedEvent.participants || selectedEvent.participants.length === 0) && (
                                                 <tr><td colSpan={(selectedEvent.questions ? selectedEvent.questions.length : 0) + 1} className="text-center">Noch keine Teilnehmer.</td></tr>
                                             )}
                                             {selectedEvent.participants && selectedEvent.participants.map((p: any) => (
                                                 <tr key={p.id}>
                                                     <td>
                                                         <strong>{p.name}</strong> <small className="text-muted">({p.email})</small>
                                                         {p.comment && <div className="small text-info">{p.comment}</div>}
                                                         <div className="mt-1">
                                                             <button className={`btn btn-sm ${p.has_no_interest ? 'btn-danger' : 'btn-outline-danger'} py-0 px-1 me-1`} style={{fontSize: '0.6rem'}} onClick={() => toggleNoInterest(p.id, selectedEvent.id)}>
                                                                 Kein Interesse
                                                             </button>
                                                             <button className="btn btn-sm btn-outline-danger py-0 px-1" style={{fontSize: '0.6rem'}} onClick={() => deleteParticipant(p.id, selectedEvent.id)}>🗑️</button>
                                                         </div>
                                                     </td>
                                                     {p.has_no_interest ? (
                                                        <td colSpan={selectedEvent.questions ? selectedEvent.questions.length : 1} className="text-danger fw-bold bg-danger-subtle align-middle fs-6">
                                                            Kein Interesse am Thema
                                                        </td>
                                                     ) : (
                                                        selectedEvent.questions && selectedEvent.questions.map((q: any) => {
                                                            const ans = p.feedback_answers ? p.feedback_answers.find((a: any) => a.question_id === q.id) : null;
                                                            return (
                                                                <td key={q.id}>
                                                                    {ans ? (q.type === 'stars' ? '⭐'.repeat(ans.value_int) : ans.value_text) : <span className="text-muted">-</span>}
                                                                </td>
                                                            );
                                                        })
                                                     )}
                                                 </tr>
                                             ))}
                                         </tbody>
                                     </table>
                                 </div>
                             </div>
                         ) : (
                         <div className="table-responsive">
                            <table className="table text-center align-middle">
                                <thead>
                                    <tr>
                                        <th>Teilnehmer</th>
                                        {Array.isArray(selectedEvent.options) && selectedEvent.options.map((opt: any) => (
                                            <th key={opt.id} className={selectedEvent.final_option_id === opt.id ? 'table-success' : ''} style={{position: 'relative'}}>
                                                {!selectedEvent.final_option_id && (
                                                    <button className="btn btn-xs btn-outline-danger py-0 px-1" style={{position: 'absolute', top: 0, right: 0, fontSize: '0.6rem'}} onClick={() => deleteOption(opt.id)}>🗑️</button>
                                                )}
                                                <div className="d-flex justify-content-center gap-1 mb-1">
                                                    <button className="btn btn-xs btn-outline-secondary py-0 px-1" style={{fontSize: '0.6rem'}} onClick={() => moveOption(opt.id, 'up')}>←</button>
                                                    <button className="btn btn-xs btn-outline-secondary py-0 px-1" style={{fontSize: '0.6rem'}} onClick={() => moveOption(opt.id, 'down')}>→</button>
                                                </div>
                                                <small>{formatOptionHeader(opt)}</small>
                                                {selectedEvent.final_option_id === opt.id && <div className="badge bg-success d-block mt-1">FINAL</div>}
                                                {selectedEvent.final_option_id !== opt.id && (
                                                    <button
                                                        className="btn btn-sm btn-outline-warning d-block w-100 mt-1 py-0"
                                                        title={selectedEvent.final_option_id ? "Finalen Termin ändern" : "Diesen Termin fixieren"}
                                                        onClick={() => finalizeEvent(selectedEvent.id, opt.id)}
                                                    >👑</button>
                                                )}                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {(!Array.isArray(selectedEvent.participants) || selectedEvent.participants.length === 0) && (
                                        <tr><td colSpan={(Array.isArray(selectedEvent.options) ? selectedEvent.options.length : 0) + 1}>Noch keine Teilnehmer.</td></tr>
                                    )}
                                    
                                    {Array.isArray(selectedEvent.participants) && selectedEvent.participants.map((p: any, index: number) => (
                                        <React.Fragment key={p.id}>
                                        {index > 0 && index % 5 === 0 && (
                                            <tr className="border-bottom">
                                                <th className="text-center"><em>Zwischenstand</em></th>
                                                {Array.isArray(selectedEvent.options) && selectedEvent.options.map((opt: any) => (
                                                    <th key={`header-${index}-${opt.id}`} className="fs-6">
                                                        <small>{formatOptionHeader(opt)}</small>
                                                    </th>
                                                ))}
                                            </tr>
                                        )}
                                        <tr>
                                            <td className="text-start">
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div className="d-flex align-items-center gap-2">
                                                        {editingParticipant === p.id ? (
                                                            <div className="input-group input-group-sm" style={{maxWidth: '150px'}}>
                                                                <input 
                                                                    type="text" 
                                                                    className="form-control" 
                                                                    value={editName} 
                                                                    onChange={e => setEditName(e.target.value)}
                                                                    onKeyDown={e => { if (e.key === 'Enter') saveParticipantName(p.id); }}
                                                                    autoFocus
                                                                />
                                                                <button className="btn btn-success" onClick={() => saveParticipantName(p.id)}>✓</button>
                                                                <button className="btn btn-secondary" onClick={() => setEditingParticipant(null)}>✗</button>
                                                            </div>
                                                        ) : (
                                                            <strong 
                                                                onClick={() => startEditingParticipant(p)} 
                                                                style={{cursor: 'pointer', borderBottom: '1px dashed #ccc'}} 
                                                                title="Klicken zum Bearbeiten"
                                                            >
                                                                {p.name}
                                                            </strong>
                                                        )}
                                                        {p.is_mandatory ? <span className="badge bg-danger ms-2" title="Pflicht-Teilnehmer">PFLICHT</span> : ''}
                                                        {p.email_status === 'failed' && <span className="badge bg-danger ms-2" title={p.email_error}>⚠️ EMAIL FEHLER</span>}
                                                        {p.email_status === 'sent' && <span className="badge bg-success ms-1" title="Gesendet">✉️</span>}
                                                    </div>
                                                    <div>
                                                                                                    {p.email_status === 'failed' && (
                                                                                                        <button className="btn btn-sm btn-warning py-0 px-1 me-1" style={{fontSize: '0.6rem'}} onClick={() => retryInvite(p.id)}>
                                                                                                            🔄 Retry
                                                                                                        </button>
                                                                                                    )}
                                                                                                    <button className="btn btn-sm btn-outline-secondary py-0 px-1 me-1" style={{fontSize: '0.6rem'}} onClick={() => toggleMandatory(p.id, selectedEvent.id)}>
                                                                                                        {p.is_mandatory ? 'Freiwillig' : 'Pflicht'}
                                                                                                    </button>
                                                                                                    <button className={`btn btn-sm ${p.has_no_interest ? 'btn-danger' : 'btn-outline-danger'} py-0 px-1 me-1`} style={{fontSize: '0.6rem'}} onClick={() => toggleNoInterest(p.id, selectedEvent.id)}>
                                                                                                        Kein Interesse
                                                                                                    </button>
                                                                                                    <button className="btn btn-sm btn-outline-danger py-0 px-1" style={{fontSize: '0.6rem'}} onClick={() => deleteParticipant(p.id, selectedEvent.id)}>🗑️</button>
                                                                                                </div>
                                                                                            </div>                                                <small className="text-muted">{p.email}</small>
                                                {p.last_reminded_at && <div className="text-warning small" style={{fontSize: '0.7rem'}}>Erinnert: {new Date(p.last_reminded_at).toLocaleDateString()}</div>}
                                                {p.comment && <div className="alert alert-info py-0 px-1 mt-1 mb-0 small">{p.comment}</div>}
                                            </td>
                                            {p.has_no_interest === 1 ? (
                                            <td colSpan={Array.isArray(selectedEvent.options) ? selectedEvent.options.length : 1} className="text-danger fw-bold bg-danger-subtle align-middle fs-6">
                                                Kein Interesse am Thema
                                            </td>
                                        ) : (
                                            Array.isArray(selectedEvent.options) && selectedEvent.options.map((opt: any) => {
                                                const vote = Array.isArray(p.votes) ? p.votes.find((v: any) => v.option_id === opt.id) : null;
                                                let icon = '❔';
                                                let color = 'text-muted';
                                                
                                                if (selectedEvent.voting_type === 'priority') {
                                                    if (vote?.choice === 'yes') { icon = '🥇'; color = 'text-success'; }
                                                    if (vote?.choice === 'no') { icon = '❌'; color = 'text-danger'; }
                                                    if (vote?.choice === 'maybe') { icon = '🥈'; color = 'text-warning'; }
                                                } else {
                                                    if (vote?.choice === 'yes') { icon = '✅'; color = 'text-success'; }
                                                    if (vote?.choice === 'no') { icon = '❌'; color = 'text-danger'; }
                                                    if (vote?.choice === 'maybe') { icon = '⚠️'; color = 'text-warning'; }
                                                }
                                                return (
                                                    <td 
                                                        key={opt.id} 
                                                        className={`fs-5 ${color}`} 
                                                        style={{cursor: editMode ? 'pointer' : 'default'}} 
                                                        onClick={(e) => editMode && handleVotePopup(e, p, opt.id)}
                                                        title={editMode ? "Klicken zum Ändern" : "Bearbeitungsmodus deaktiviert"}
                                                    >
                                                        {icon}
                                                    </td>
                                                );
                                            })
                                        )}
                                        </tr>
                                        </React.Fragment>
                                    ))}
                                    
                                    {/* SUM ROW */}
                                    <tr className="fw-bold">
                                        <td>
                                            Summe
                                            {selectedEvent.voting_type === 'priority' && (
                                                <div className="mt-1">
                                                    <select 
                                                        className="form-select form-select-sm fw-normal text-muted border-secondary-subtle" 
                                                        style={{ fontSize: '0.7rem', padding: '0.1rem 1.5rem 0.1rem 0.5rem', width: 'auto' }}
                                                        value={prioritySortMode}
                                                        onChange={(e) => setPrioritySortMode(e.target.value as any)}
                                                    >
                                                        <option value="total">Bester: Gesamt</option>
                                                        <option value="first">Bester: 1. Wahl</option>
                                                        <option value="second">Bester: 2. Wahl</option>
                                                    </select>
                                                </div>
                                            )}
                                        </td>
                                        {Array.isArray(selectedEvent.options) && selectedEvent.options.map((opt: any) => {
                                            const getPriorityCounts = (oId: number) => {
                                                if (!Array.isArray(selectedEvent.participants)) return { first: 0, second: 0, total: 0 };
                                                return selectedEvent.participants.reduce((acc: any, p: any) => {
                                                    const v = Array.isArray(p.votes) ? p.votes.find((vote: any) => vote.option_id === oId) : null;
                                                    if (v?.choice === 'yes') { acc.first++; acc.total++; }
                                                    if (v?.choice === 'maybe') { acc.second++; acc.total++; }
                                                    return acc;
                                                }, { first: 0, second: 0, total: 0 });
                                            };

                                            const calculateSimpleScore = (oId: number) => {
                                                if (!Array.isArray(selectedEvent.participants)) return 0;
                                                return selectedEvent.participants.reduce((acc: number, p: any) => {
                                                    const v = Array.isArray(p.votes) ? p.votes.find((vote: any) => vote.option_id === oId) : null;
                                                    if (v?.choice === 'yes') return acc + 1;
                                                    if (v?.choice === 'maybe') return acc + 0.75;
                                                    return acc;
                                                }, 0);
                                            };

                                            const isPriority = selectedEvent.voting_type === 'priority';
                                            const counts = getPriorityCounts(opt.id);
                                            const simpleScore = calculateSimpleScore(opt.id);

                                            // Best Option Logic
                                            let isBest = false;
                                            if (isPriority) {
                                                const allCounts = selectedEvent.options.map((o: any) => getPriorityCounts(o.id));
                                                const maxVal = Math.max(...allCounts.map((c: any) => c[prioritySortMode]));
                                                isBest = counts[prioritySortMode] === maxVal && maxVal > 0;
                                            } else {
                                                const maxVotes = Math.max(...selectedEvent.options.map((o: any) => calculateSimpleScore(o.id)));
                                                isBest = simpleScore === maxVotes && maxVotes > 0;
                                            }

                                            // Quorum Logic
                                            const missingMandatory = selectedEvent.participants.filter((p: any) => p.is_mandatory && !p.votes.find((v: any) => v.option_id === opt.id && v.choice === 'yes'));
                                            const quorumFailed = missingMandatory.length > 0;

                                            return (
                                                <td key={opt.id} className={`text-success ${isBest ? 'bg-success bg-opacity-10 border border-success' : ''}`}>
                                                    {isPriority ? (
                                                        <div className="d-flex align-items-center justify-content-center gap-1" style={{ fontSize: '0.9rem' }}>
                                                            <span title="1. Wahl">{counts.first} 🥇</span>
                                                            <span className="text-muted fw-normal" style={{ fontSize: '0.7rem' }}>/</span>
                                                            <span title="2. Wahl" className="text-warning">{counts.second} 🥈</span>
                                                        </div>
                                                    ) : (
                                                        simpleScore
                                                    )}
                                                    {isBest && <div className="small text-success fw-bold mt-1" style={{fontSize: '0.6rem'}}>BESTER TERMIN</div>}
                                                    {quorumFailed && <div className="text-danger" style={{fontSize: '0.65rem'}} title={`Fehlende Pflicht-Teilnehmer: ${missingMandatory.map((m: any) => m.name).join(', ')}`}>⚠️ QUORUM</div>}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        )}
                        {!selectedEvent.final_option_id && selectedEvent.voting_type !== 'order' && selectedEvent.voting_type !== 'feedback' && (
                            <div className="card mt-4 border-info">
                                <div className="card-header bg-info text-white py-2">
                                    <h6 className="mb-0">➕ Weiteren Termin hinzufügen</h6>
                                </div>
                                <div className="card-body py-2">
                                    <div className="row g-2 align-items-end">
                                        <div className="col-md-3">
                                            <label className="form-label small mb-0">Datum Start</label>
                                            <input type="date" className="form-control form-control-sm" value={inputDate} onChange={e => setInputDate(e.target.value)} />
                                        </div>
                                        <div className="col-md-2">
                                            <label className="form-label small mb-0">Von</label>
                                            <input type="time" className="form-control form-control-sm" value={inputStart} onChange={e => setInputStart(e.target.value)} />
                                        </div>
                                        <div className="col-md-3">
                                            <label className="form-label small mb-0">Datum Ende (Optional)</label>
                                            <input type="date" className="form-control form-control-sm" value={inputEndDate} onChange={e => setInputEndDate(e.target.value)} />
                                        </div>
                                        <div className="col-md-2">
                                            <label className="form-label small mb-0">Bis</label>
                                            <input type="time" className="form-control form-control-sm" value={inputEnd} onChange={e => setInputEnd(e.target.value)} />
                                        </div>
                                        <div className="col-md-2">
                                            <button className="btn btn-sm btn-primary w-100" onClick={addOptionToExistingEvent} disabled={!inputDate || !inputStart}>Hinzufügen</button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            )}
            {editPopup && (
                <>
                    <div style={{position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1040}} onClick={() => setEditPopup(null)}></div>
                    <div className="btn-group shadow" style={{
                        position: 'fixed', 
                        top: editPopup.y, 
                        left: editPopup.x, 
                        zIndex: 1050, 
                        backgroundColor: 'white'
                    }}>
                    {selectedEvent.voting_type === 'priority' ? (
                        <>
                            <button className="btn btn-sm btn-outline-success" onClick={() => handleVoteSelect('yes')} title="1. Wahl">🥇</button>
                            <button className="btn btn-sm btn-outline-warning" onClick={() => handleVoteSelect('maybe')} title="2. Wahl">🥈</button>
                            <button className="btn btn-sm btn-outline-danger" onClick={() => handleVoteSelect('no')} title="Nein">❌</button>
                        </>
                    ) : (
                        <>
                            <button className="btn btn-sm btn-outline-success" onClick={() => handleVoteSelect('yes')} title="Ja">✅</button>
                            <button className="btn btn-sm btn-outline-warning" onClick={() => handleVoteSelect('maybe')} title="Vielleicht">⚠️</button>
                            <button className="btn btn-sm btn-outline-danger" onClick={() => handleVoteSelect('no')} title="Nein">❌</button>
                        </>
                    )}
                    </div>
                </>
            )}
        </div>
    );
};

export default AdminDashboard;